import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import axios from "../../axios/axios";

import CompanyLogo from "../../Assets/icons/logo_large_black.svg";
import { handleError } from "../../axios/handleError";
import {
  authRegex,
  constants,
  loginPreferenceOptions,
  methodSpecificOptions,
} from "../../utility/constants/constants";
import Container from "../components/Container";
import CustomSwitch from "../components/CustomSwitch/CustomSwitch";
import InputPassword from "../components/InputPassword";
import { unstable_batchedUpdates } from "react-dom";

const Login = () => {
  const [email, setEmail] = useState("");
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [sendOtpForLoading, setSendOtpLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [otpStatus, setOtpStatus] = useState(false);
  const [showSSOOtp, setShowSSOOtp] = useState(false);


  const [isPassword2FAOn, setIsPassword2FAOn] = useState(false); //for 2fa
  const navigate = useNavigate();
  const [loginPreference, setLoginPreference] = useState(
    localStorage.getItem("loginPreference") ?? "otp"
  );

  const [counter, setCounter] = useState(0);

  const twoFAOtpRef = useRef(null);

  const sso2fa = searchParams.get('sso2fa')
  const token = searchParams.get('token')
  const loginMethod = searchParams.get('method')
  const sso_org_id = searchParams.get('org_id')
  const loginOption = !loginMethod ? loginPreferenceOptions : methodSpecificOptions[loginMethod]

  const productUrls = {
    litigation: process.env.REACT_APP_LITIGATION_FRONTEND_URL,
    contract: process.env.REACT_APP_CONTRACT_FRONTEND_URL,
    ipr: process.env.REACT_APP_IPR_FRONTEND_URL,
    notice: process.env.REACT_APP_NOTICE_FRONTEND_URL,
    advisory: process.env.REACT_APP_ADVISORY_FRONTEND_URL,
    arbitration: process.env.REACT_APP_ARBITRATION_FRONTEND_URL,
  };

  const redirectToApp = (id) => {
    let dynamicRedirectUrls = null;
    try {
      dynamicRedirectUrls = JSON.parse(localStorage.getItem("dynamicRedirectUrls") || "null");
    } catch (error) {
      dynamicRedirectUrls = null;
    }
    const url = dynamicRedirectUrls?.[id] || productUrls[id];
    window.location.replace(url);
  };

  useEffect(() => {
    const timer =
      counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]);

  const authCookie = document.cookie.replace(authRegex, "$1");

  useEffect(() => {
    if (authCookie === "true") {
      if (searchParams.get("redirect_url") && searchParams.get("service"))
        navigate(
          "/?redirect_url=" +
          searchParams.get("redirect_url") +
          "&service=" +
          searchParams.get("service")
        );
      else navigate("/");
    } else
      document.cookie = `auth=; Path=/; domain=${process.env.REACT_APP_COOKIE_DOMAIN}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendOtp = async (type) => {
    if (counter) {
      toast.error(`You can resend after ${counter} seconds`);
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error("Enter Valid Email");
      return;
    }
    setSendOtpLoading(true);
    const bodyData = { email: email.trim() };
    if (type === "is2Fa") bodyData.is2Fa = true;
    try {
      const response = await axios.post(constants.SEND_OTP, bodyData);
      if (response?.data?.status) {
        setOtpStatus(true);
        toast.success("OTP Sent!");
        setCounter(60);
      } else {
        toast.error(response?.data?.msg || "User not found");
      }
    } catch (err) {
      handleError(err);
    }
    setSendOtpLoading(false);
  };

  const emailKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!otpStatus) sendOtp();
    }
  };

  const handlePassSubmit = async (e) => {
    e.preventDefault();
    const data = { email: email.trim(), password };
    if (otp && isPassword2FAOn) data.otp2Fa = otp;

    setLoginLoading(true);
    try {
      const response = await axios.post(constants.LOGIN_USER, data, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      if (response.status === 200) {
        localStorage.setItem("loginPreference", loginPreference);
        localStorage.setItem(
          "dynamicRedirectUrls",
          JSON.stringify(response?.data?.data?.applicationRedirectUrls || null)
        );
        if (response?.data?.data?.is2FaEnabled) {
          setIsPassword2FAOn(true);
          toast?.success("OTP Sent To Your Registered Email");
          return;
        }
        if (response?.data?.data?.pinnedProduct) {
          return redirectToApp(response?.data?.data?.pinnedProduct);
        } else if (response?.data?.data?.accessToProducts?.length === 1) {
          return redirectToApp(response?.data?.data?.accessToProducts[0]);
        }

        if (searchParams.get("redirect_url") && searchParams.get("service"))
          return navigate(
            "/?redirect_url=" +
            searchParams.get("redirect_url") +
            "&service=" +
            searchParams.get("service")
          );

        if (response?.data?.data?.pinnedProduct === "") {
          return navigate("/");
        }
      } else toast.error("Invalid OTP!!");
    } catch (err) {
      handleError(err);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otpStatus) return;
    setLoginLoading(true);
    try {
      const response = await axios.post(
        constants.LOGIN_USER,
        { email: email.trim(), otp },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      if (response.status === 200) {
        localStorage.setItem("loginPreference", loginPreference);
        localStorage.setItem(
          "dynamicRedirectUrls",
          JSON.stringify(response?.data?.data?.applicationRedirectUrls || null)
        );
        if (response?.data?.data?.pinnedProduct) {
          return redirectToApp(response?.data?.data?.pinnedProduct);
        } else if (response?.data?.data?.accessToProducts?.length === 1) {
          return redirectToApp(response?.data?.data?.accessToProducts[0]);
        }
        if (searchParams.get("redirect_url") && searchParams.get("service"))
          return navigate(
            "/?redirect_url=" +
            searchParams.get("redirect_url") +
            "&service=" +
            searchParams.get("service")
          );
        if (response?.data?.data?.pinnedProduct === "") {
          return navigate("/");
        }
      } else toast.error("Invalid OTP!!");
    } catch (err) {
      handleError(err);
    }
    setLoginLoading(false);
  };

  const handlePreference = (value) => {
    setLoginPreference(value);
    setEmail("");
    setOtp("");
    setPassword("");
    setOtpStatus(false);
    setIsPassword2FAOn(false);
  };

  const handleSSOSubmit = async (e, orgId = sso_org_id) => {
    e?.preventDefault();
    setSendOtpLoading(true);
    try {
      const id = orgId ? `&org_id=${orgId}` : `email=${email}`
      const response = await axios.get(
        `${constants.SSO_LOGIN}?${id}&authType=activeDirectorySAML`
      );
      if (response?.data?.status) {
        localStorage.setItem("loginPreference", loginPreference);
        if (response?.data?.data && response?.data?.data?.loginUrl)
          window.location.replace(
            `${response.data.data.loginUrl}?userEmail=${email}`
          );
      }
    } catch (err) {
      handleError(err);
    }
    setSendOtpLoading(false);
  };

  const handleSSOOtp = async (e) => {
    e.preventDefault();
    setSendOtpLoading(true);
    try {
      const payload = { otp2Fa: otp, token }
      const response = await axios.post(constants.TOW_FA_SS0_OTP_VALIDATE, payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      if (response.status === 200) {
        localStorage.setItem("loginPreference", loginPreference);
        if (response?.data?.data?.pinnedProduct) {
          return redirectToApp(response?.data?.data?.pinnedProduct);
        } else if (response?.data?.data?.accessToProducts?.length === 1) {
          return redirectToApp(response?.data?.data?.accessToProducts[0]);
        }

        if (response?.data?.data?.pinnedProduct === "") {
          return navigate("/");
        }
      } else toast.error("Invalid OTP!!");
    } catch (err) {
      handleError(err);
    }
    setSendOtpLoading(false);
  }

  useEffect(() => {
    if (twoFAOtpRef.current) {
      twoFAOtpRef.current.focus(); // Focus the input when the component mounts
    }
  }, [isPassword2FAOn]);

  useEffect(() => {
    if (sso2fa === 'true' && token) {
      unstable_batchedUpdates(() => {
        setLoginPreference('sso')
        setShowSSOOtp(true)
      })
    }
  }, [sso2fa, token])
  useEffect(() => {
    if (loginMethod === 'sso' && sso_org_id) handleSSOSubmit(null, sso_org_id)
    if (loginMethod) setLoginPreference(loginMethod)

  }, [loginMethod, sso_org_id])
  return (
    <Container>
      <div className="m-3 m-md-5 w-100">
        <img
          className="mb-4 cursor-pointer"
          onClick={() => window.open(constants.LANDING_PAGE_URL)}
          src={CompanyLogo}
          alt="logo"
        />
        <p className="h5 mb-4 fw-900">Login to access LegisTrak</p>
        {(!isPassword2FAOn && !showSSOOtp && !loginMethod) && (
          <CustomSwitch
            className="mb-4"
            options={loginOption}
            name="loginPreference"
            id="loginPreference"
            value={loginPreference}
            onChange={handlePreference}
          />
        )}
        {/*opt ======================================== */}
        {loginPreference === "otp" && (
          <form
            className="d-flex flex-column justify-content-between"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="mb-4 position-relative">
              <input
                autoFocus
                placeholder="Registered Email"
                name="email"
                id="email"
                type="email"
                value={email}
                disabled={otpStatus}
                onKeyDown={emailKeyDown}
                onChange={(e) => setEmail(e.target.value)}
                className="form-control"
                required
                autoComplete="off"
              />
            </div>
            {!otpStatus && (
              <button
                id="otp_send-otp-btn"
                onClick={sendOtp}
                className="btn btn-primary"
                type="button"
                disabled={sendOtpForLoading}
              >
                {sendOtpForLoading ? (
                  <Spinner size="sm" className="m-1" />
                ) : (
                  "NEXT"
                )}
              </button>
            )}
            {otpStatus && (
              <>
                <div className="mb-4">
                  <input
                    placeholder="Enter OTP"
                    autoFocus
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    type="text"
                    className="form-control"
                  />
                  <div className="d-flex justify-content-end">
                    {sendOtpForLoading ? (
                      <Spinner size="sm" className="mt-1" />
                    ) : (
                      <p
                        id="otp_resend-otp-btn"
                        className="mb-0 text-primary fs-6 cursor-pointer"
                        onClick={() => sendOtp()}
                      >
                        Resend OTP
                      </p>
                    )}
                  </div>
                </div>
                <button
                  id="otp_login-btn"
                  type="submit"
                  className="btn btn-primary mt-1"
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <Spinner size="sm" className="m-1" />
                  ) : (
                    "LOGIN"
                  )}
                </button>
              </>
            )}
          </form>
        )}
        {/*password ======================================== */}
        {loginPreference === "pass" && (
          <form
            className="d-flex flex-column justify-content-between"
            onSubmit={handlePassSubmit}
          >
            {!isPassword2FAOn ? (
              <>
                <div className="mb-4">
                  <input
                    autoFocus
                    required
                    type="email"
                    autoComplete="off"
                    pattern="^\S+@\S+\.\S+$"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Registered Email"
                    className="form-control"
                  />
                </div>
                <div className="mb-4">
                  <InputPassword
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="form-control"
                  />
                  <p
                    id="forgot_password_btn"
                    style={{ width: "fit-content" }}
                    className="ms-auto text-end mb-0 text-primary cursor-pointer"
                    onClick={() => navigate("/forget-password")}
                  >
                    Forgot Password
                  </p>
                </div>
                <button
                  id="password_login-btn"
                  type="submit"
                  className="btn btn-primary"
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <Spinner size="sm" className="m-1" />
                  ) : (
                    "LOGIN"
                  )}
                </button>
              </>
            ) : (
              <>
                <div className=" position-relative">
                  <input
                    placeholder="Enter OTP Sent On Your Mail"
                    autoFocus
                    id="otp"
                    ref={twoFAOtpRef}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    type="text"
                    className="form-control"
                  />
                </div>
                <div className="d-flex justify-content-end mb-4">
                  {sendOtpForLoading ? (
                    <Spinner size="sm" className="mt-1" />
                  ) : (
                    <p
                      id="password_resend-otp-btn"
                      className="mb-0 text-primary fs-6 cursor-pointer"
                      onClick={() => sendOtp("is2Fa")}
                    >
                      Resend OTP
                    </p>
                  )}
                </div>
                <button
                  id="password_validate-otp-btn"
                  type="submit"
                  className="btn btn-primary"
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <Spinner size="sm" className="m-1 fw-bold" />
                  ) : (
                    "Continue"
                  )}
                </button>
              </>
            )}
          </form>
        )}
        {/*sso ======================================== */}
        {loginPreference === "sso" && (
          <form
            className="d-flex flex-column justify-content-between"
            onSubmit={!showSSOOtp ? handleSSOSubmit : handleSSOOtp}
          >
            {!showSSOOtp ? <>
              <div className="mb-4 position-relative">
                <input
                  autoComplete="off"
                  autoFocus
                  placeholder="Registered Email"
                  name="email"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-control"
                  required
                />
              </div>
              <button
                id="sso_send-otp-btn"
                type="submit"
                className="btn btn-primary"
                disabled={loginLoading}
              >
                {loginLoading ? <Spinner size="sm" className="m-1" /> : "NEXT"}
              </button>
            </> :
              <>
                <div className="mb-4">
                  <input
                    placeholder="Enter OTP"
                    autoFocus
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    type="text"
                    className="form-control"
                  />
                </div>
                <button
                 id="sso_login-btn"
                  type="submit"
                  className="btn btn-primary mt-1 w-100"
                  disabled={sendOtpForLoading}
                >
                  {sendOtpForLoading ? (
                    <Spinner size="sm" className="m-1" />
                  ) : (
                    "LOGIN"
                  )}
                </button>
              </>
            }
          </form>
        )}
      </div>
    </Container>
  );
};

export default Login;
